<template>
  <app-wrapper>
    <app-header :loan="loan" medium-max-width="max-w-1000">
      <template #navigation>
        <div class="max-w-200">
          <h5 class="font-zen-dots text-white">
            <AppLogo/>
          </h5>
        </div>
      </template>
      <template #top>
        <div class="w-full flex items-center">
          <div class="flex-auto">
            Your desired loan amount
            <h2 class="mb-0 mt-2 md:mt-4 md:text-6xl">{{ loan.amount | money(0) }}</h2>
          </div>
          <div class="flex-initial">
            <i class="fa fa-check-circle text-success text-7xl"/>
          </div>
        </div>
      </template>
    </app-header>

    <app-body medium-max-width="max-w-500" footer>
      <template #title>
        <template v-if="garnishmentFound">Your loan is currently pending</template>
        <template v-else-if="manualReviewPending">Your application is under review</template>
        <template v-else>Upload Your Paystub</template>
      </template>

      <template #text>
        <div v-if="garnishmentFound">
          Your loan is under review. We will reach out if we need more information to process your request. If you have questions or need assistance please contact us at
          <a v-if="$root.app?.support_email" :href="$root.app.mailtoSupport">{{ $root.app.support_email }}</a>.
        </div>

        <div v-else-if="manualReviewPending">
          <template v-if="payerChangeResolvable">
            <p>We found the income on your paystub — but not in your selected bank account.</p>
            <p class="mt-2">Select the account where this income is deposited to continue verification.</p>
            <a
              data-testid="change-income-account"
              href="/account/account?edit-billing=1&amp;change-bank=1"
              class="inline-block mt-4 bg-primary hover:opacity-90 text-white font-medium py-3 px-6 rounded-lg transition no-underline"
            >
              Change income account
            </a>
          </template>
          <p v-else>Your application is under review — a team member will reach out shortly.</p>

          <p class="mt-4">
            Questions? Contact us at
            <a v-if="$root.app?.support_email" :href="$root.app.mailtoSupport">{{ $root.app.support_email }}</a>.
          </p>
        </div>

        <!--
          Cap branch. Rendered BEFORE the regular errorMessage block, so anything the borrower is
          meant to learn about the last rejection has to be repeated here — otherwise the reason is
          silently masked (Case D: three "not a paystub" uploads, borrower only ever saw "contact
          support"). capReasonMessage states the reason without asking for an upload the borrower
          can no longer make.
        -->
        <div v-else-if="limitReached" class="mb-4">
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex items-start">
              <i class="fa fa-exclamation-circle text-red-500 text-xl mr-3 mt-1"></i>
              <div class="text-red-700">
                <p v-if="capReasonMessage" class="mb-2">{{ capReasonMessage }}</p>
                <p>
                  You've reached the maximum number of upload attempts. Please contact support for assistance.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <div v-if="errorMessage" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div class="flex items-start">
              <i class="fa fa-exclamation-circle text-red-500 text-xl mr-3 mt-1"></i>
              <p class="text-red-700">{{ errorMessage }}</p>
            </div>
          </div>

          <p class="mb-2">Please upload a clear image or PDF of your most recent paystub.</p>
          <p class="text-sm text-gray-500 mb-4">
            Accepted formats: PDF, JPG, JPEG, PNG (max 20MB).
            <span v-if="remainingAttempts < 3">{{ remainingAttempts }} of 3 attempts remaining.</span>
          </p>
        </div>
      </template>

      <template #main v-if="!limitReached && !garnishmentFound && !manualReviewPending">
        <div class="mt-4">
          <!-- Drop zone -->
          <div
            class="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors"
            :class="{
              'border-blue-400 bg-blue-50': isDragging,
              'border-gray-300 hover:border-gray-400': !isDragging,
              'opacity-50 pointer-events-none': uploading
            }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            @click="$refs.fileInput.click()"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              class="hidden"
              @change="handleFileSelect"
            />

            <div v-if="!selectedFile">
              <i class="fa fa-cloud-upload text-4xl text-gray-400 mb-3"></i>
              <p class="text-gray-600">Drag & drop your file here or click to browse</p>
            </div>

            <div v-else>
              <i class="fa fa-file text-4xl text-blue-500 mb-3"></i>
              <p class="text-gray-800 font-medium">{{ selectedFile.name }}</p>
              <p class="text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
            </div>
          </div>

          <!-- Validation error -->
          <p v-if="validationError" class="text-red-600 text-sm mt-2">{{ validationError }}</p>
          <p v-if="uploadError" class="text-red-600 text-sm mt-2">{{ uploadError }}</p>

          <!-- Upload button -->
          <button
            v-if="selectedFile && !uploading"
            @click="uploadFile"
            class="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
          >
            Upload Paystub
          </button>

          <!-- Loading state -->
          <div v-if="uploading" class="mt-4 text-center">
            <i class="fa fa-spinner fa-spin text-2xl text-blue-500"></i>
            <p class="text-gray-600 mt-2">Uploading...</p>
          </div>
        </div>
      </template>
    </app-body>
  </app-wrapper>
</template>

<script>
import AppLogo from './../../../components/AppLogo';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_SIZE = 20 * 1024 * 1024; // 20MB

export default {
  mixins: [
    require('../../../mixins/InteractsWithGtag').default,
    require('../../../mixins/PageViewAnalytics').default
  ],
  components: {
    AppLogo,
  },
  props: {
    user: {},
    loan: {},
    remainingAttempts: {
      type: Number,
      default: 3,
    },
    errorMessage: {
      type: String,
      default: null,
    },
    // Cap-safe explanation of the latest rejection, shown only in the limitReached branch.
    capReasonMessage: {
      type: String,
      default: null,
    },
    limitReached: {
      type: Boolean,
      default: false,
    },
    garnishmentFound: {
      type: Boolean,
      default: false,
    },
    manualReviewPending: {
      type: Boolean,
      default: false,
    },
    // Server-side verdict that the payer-change hold can actually be self-resolved (usable
    // parsed net pay + pay date). Without it the CTA would lead to guaranteed rejections.
    payerChangeResolvable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      gTagPageName: 'CUSTOM_PAYSTUB_UPLOAD',
      selectedFile: null,
      isDragging: false,
      uploading: false,
      validationError: null,
      uploadError: null,
    };
  },

  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) this.setFile(file);
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) this.setFile(file);
    },

    setFile(file) {
      this.validationError = null;
      this.uploadError = null;

      if (!ALLOWED_TYPES.includes(file.type)) {
        this.validationError = 'Please select a PDF, JPG, JPEG, or PNG file.';
        this.selectedFile = null;
        return;
      }

      if (file.size > MAX_SIZE) {
        this.validationError = 'File size must be less than 20MB.';
        this.selectedFile = null;
        return;
      }

      this.selectedFile = file;
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    async uploadFile() {
      if (!this.selectedFile || this.uploading) return;

      this.uploading = true;
      this.uploadError = null;

      const formData = new FormData();
      formData.append('file', this.selectedFile);

      try {
        await axios.post(`/signup/custom-paystub-upload/${this.loan.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        window.location.reload();
      } catch (error) {
        this.uploading = false;
        if (error.response && error.response.data) {
          const data = error.response.data;
          if (data.errors && data.errors.file) {
            this.uploadError = data.errors.file[0];
          } else if (data.error) {
            this.uploadError = data.error;
          } else {
            this.uploadError = 'An error occurred while uploading. Please try again.';
          }
        } else {
          this.uploadError = 'An error occurred while uploading. Please try again.';
        }
      }
    },
  },
};
</script>
